
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Copy, Eye, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useState } from "react";
import { WorkoutDetails } from "./WorkoutDetails";
import { Switch } from "@/components/ui/switch";

interface WorkoutActionsProps {
  workout: any;
  onToggleStatus: (workoutId: string, status: string) => void;
  onDuplicate: (workout: any) => void;
  onDelete: (workoutId: string) => void;
  onDownloadPDF?: (workout: any) => void; // Add this prop for PDF download
}

export const WorkoutActions = ({
  workout,
  onToggleStatus,
  onDuplicate,
  onDelete,
  onDownloadPDF, // Accept the prop
}: WorkoutActionsProps) => {
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("workouts");
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const isActive = workout.status === "active";
  
  return (
    <>
      <div className="flex flex-wrap justify-end items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {isActive ? t("status.active") : t("status.inactive")}
          </span>
          <Switch 
            checked={isActive}
            onCheckedChange={() => onToggleStatus(workout.id, workout.status)}
            className="data-[state=checked]:bg-green-500 dark:data-[state=checked]:bg-green-600"
          />
        </div>
        
        <div className="flex items-center gap-1">
          {onDownloadPDF && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDownloadPDF(workout)}
              className="h-8 w-8 text-muted-foreground hover:text-primary"
              title={t("actions.downloadPDF")}
            >
              <FileText className="h-4 w-4" />
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDetailsOpen(true)}
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            title={t("actions.viewDetails")}
          >
            <Eye className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(getPath(`/dashboard/workouts/edit/${workout.id}`))}
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            title={t("actions.edit")}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDuplicate(workout)}
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            title={t("actions.duplicate")}
          >
            <Copy className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(workout.id)}
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title={t("actions.delete")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <WorkoutDetails 
        workout={workout} 
        isOpen={isDetailsOpen} 
        onClose={() => setIsDetailsOpen(false)} 
      />
    </>
  );
};
